// src/components/CardPage.js - SIMPLIFIED FOR PHYSICAL CARD REFERENCES
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CardDisplay from './CardDisplay';
import WorkoutProgressionModule from './WorkoutProgressionModule';
import { getAllCards } from '../data/BattleCardsData';
import { STATIC_CARD_ORDER, getCardByPosition, getImagePathByPosition, getTotalCardCount } from '../data/StaticCardMapping';
import { getCardContent, hasCardContent } from '../data/CardContent';
import { soundManager } from '../utils/SoundManager';
import './CardPage.css';

const CardPage = () => {
    const { cardNumber } = useParams();
    const navigate = useNavigate();
    const [card, setCard] = useState(null);
    const [cardContent, setCardContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadCard = () => {
            try {
                console.log('🔍 Loading card for URL:', cardNumber);

                const position = parseInt(cardNumber);
                console.log('📍 URL position:', position);

                // Get the card info from our static mapping
                const cardInfo = getCardByPosition(position);
                console.log('🎯 Card info from mapping:', cardInfo);

                if (!cardInfo) {
                    console.log('❌ Position not found in static mapping');
                    setError(`Card position ${cardNumber} not found`);
                    setLoading(false);
                    return;
                }

                // Create a virtual card object with the image info
                const virtualCard = {
                    id: cardInfo.id,
                    name: `Card ${cardNumber}`,
                    description: `This is card number ${cardNumber} showing image ${cardInfo.image}`,
                    type: cardInfo.folder === 'battle-cards' ? 'Battle' : 'Train',
                    imagePath: getImagePathByPosition(position),
                    unlocked: true, // Assume all cards are viewable
                    rarity: { name: 'Common' }
                };

                console.log('✅ Created virtual card:', virtualCard.name, 'with image:', virtualCard.imagePath);
                setCard(virtualCard);

                // Load card content (story, background, etc.)
                const content = getCardContent(cardNumber);
                setCardContent(content);
                console.log('📖 Card content loaded:', content ? 'Yes' : 'No');

                soundManager.playSound('next');

            } catch (err) {
                console.log('💥 ERROR:', err);
                setError('Error loading card');
                console.error('Error loading card:', err);
            } finally {
                setLoading(false);
            }
        };

        loadCard();
    }, [cardNumber]);

    const handlePreviousCard = () => {
        const prevNumber = Math.max(1, parseInt(cardNumber) - 1);
        navigate(`/battle-cards/${prevNumber.toString().padStart(3, '0')}`);
    };

    const handleNextCard = () => {
        const nextNumber = Math.min(getTotalCardCount() || 450, parseInt(cardNumber) + 1);
        navigate(`/battle-cards/${nextNumber.toString().padStart(3, '0')}`);
    };

    const getCardImagePath = () => {
        if (!card || !card.imagePath) return null;
        return card.imagePath;
    };

    if (loading) {
        return (
            <div className="card-page loading simplified">
                <div className="loading-spinner">Loading card...</div>
            </div>
        );
    }

    if (error || !card) {
        return (
            <div className="card-page error simplified">
                <div className="error-content">
                    <h2>Card Not Found</h2>
                    <p>{error || 'The requested card could not be found.'}</p>
                    <button
                        className="back-button"
                        onClick={() => navigate('/card-gallery')}
                    >
                        Back to Gallery
                    </button>
                </div>
            </div>
        );
    }

    const totalCards = getTotalCardCount() || 450;

    return (
        <div className="card-page simplified">
            {/* Header with Top Links and Navigation */}
            <div className="card-page-header minimal">
                <div className="top-navigation-links">
                    <a href="/train-page" className="top-nav-link">Train</a>
                    <a href="/build-page" className="top-nav-link">Build</a>
                    <a href="/battle-page" className="top-nav-link">Battle</a>
                    <a href="https://www.bumfit.com" className="top-nav-link bumfit">BumFit</a>
                </div>
                <div className="card-navigation">
                    <button
                        className="nav-button prev"
                        onClick={handlePreviousCard}
                        disabled={parseInt(cardNumber) <= 1}
                    >
                        ← Previous
                    </button>
                    <button
                        className="nav-button next"
                        onClick={handleNextCard}
                        disabled={parseInt(cardNumber) >= totalCards}
                    >
                        Next →
                    </button>
                </div>
            </div>

            {/* Full Frame Card Display */}
            <div className="card-page-content full-frame">
                <div className="card-display-section centered">
                    {/* Card Image - Full Size */}
                    <div className="card-image-container full-size">
                        <img
                            src={getCardImagePath()}
                            alt="Battle Card"
                            className="card-image full-display"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                            }}
                        />
                        <div className="image-fallback" style={{display: 'none'}}>
                            <CardDisplay
                                card={card}
                                size="large"
                                showStats={false}
                                interactive={false}
                                isOwned={card.unlocked}
                            />
                        </div>
                    </div>

                    {/* Optional: Future space for card character name and description */}
                    {cardContent && (
                        <div className="card-info-minimal">
                            <h2 className="character-name">{cardContent.characterName}</h2>
                            {cardContent.title && (
                                <h3 className="character-title">{cardContent.title}</h3>
                            )}
                            <div className="character-story">
                                <p className="story-text">{cardContent.story}</p>
                                {cardContent.background && (
                                    <p className="background-text">{cardContent.background}</p>
                                )}
                                {cardContent.philosophy && (
                                    <div className="philosophy-quote">
                                        <p>"{cardContent.philosophy}"</p>
                                    </div>
                                )}
                            </div>
                            {cardContent.specialties && cardContent.specialties.length > 0 && (
                                <div className="character-specialties">
                                    <h4>Specialties:</h4>
                                    <ul>
                                        {cardContent.specialties.map((specialty, index) => (
                                            <li key={index}>{specialty}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {/* Legacy abilities support for non-trainer cards */}
                            {cardContent.abilities && cardContent.abilities.length > 0 && (
                                <div className="character-abilities">
                                    <h4>Special Abilities:</h4>
                                    <ul>
                                        {cardContent.abilities.map((ability, index) => (
                                            <li key={index}>{ability}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <div className="character-meta">
                                {cardContent.trainingStyle && <span className="training-style">Style: {cardContent.trainingStyle}</span>}
                                {cardContent.expertise && <span className="expertise">Expertise: {cardContent.expertise}</span>}
                                {cardContent.element && <span className="element">Element: {cardContent.element}</span>}
                                {cardContent.origin && <span className="origin">Origin: {cardContent.origin}</span>}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {/* Add the workout progression module here */}
            <WorkoutProgressionModule />
        </div>
    );
};

export default CardPage;